enum AccountantQualification {
  ACA
  ACCA
  FCA
  FCCA
  ACMA
  FCMA
  CPFA
  ACPA
  FCPA
  AAPA
  AAIA
  FAIA
  FFA
  AFA
  CA
  OTHER
}

enum DepositType {
  CASH_SAVINGS
  INVESTMENT
  GIFT
  OTHER
  VENDOR_INDUCEMENT
  INVALID_DEPOSIT_TYPE
}

enum DebtType {
  INVALID_DEBT_TYPE
  INVALID_COMMITMENT_TYPE
  CREDIT_CARD
  OVERDRAFT
  STORE_CARD
  LOAN
  HIRED_PURCHASE
  SECURED_EXCHANGE
  UTILITIES
  COMMUNICATIONS
  MAIL_ORDER
  PAY_DAY
  BUY_NOW_PAY_LATER
  SECURED_CAR_LOAN
  STUDENT_LOAN
  MORTGAGE
}

enum BorrowerType {
  APPLICANT
  GUARANTOR
}

enum ClientType {
  CLIENT
  GUARANTOR
  SOURCE_OF_FUNDS
  CO_OWNER
  DUPLICATE
}

enum Title {
  MR
  MRS
  MISS
  MS
  MX
  DR
  DAME
  PROF
  SIR
  LORD
  LADY
  INVALID_TITLE
}

enum Gender {
  MALE
  FEMALE
  NOT_SPECIFIED
}

enum MaritalStatus {
  SINGLE
  MARRIED
  SEPARATED
  DIVORCED
  WIDOWED
  COHABIT
  CIVIL_PARTNERSHIP
  INVALID_MARITAL_STATUS
}

enum ResidencyStatus {
  UK_CITIZEN
  EU_CITIZEN
  IRISH_CITIZEN
  VISA
  VISA_2YRS_PLUS
  LEAVE_TO_REMAIN
}

enum EmploymentStatus {
  EMPLOYED
  FIXED_TERM_CONTRACTOR
  FIXED_TERM_CONTRACTOR_PAYE
  HOMEMAKER
  RETIRED
  SELF_EMPLOYED
  INVALID_EMPLOYMENT_STATUS
}

enum DependantRelationship {
  CHILD
  SPOUSE
  SIBLING
  PARENT
  OTHER
  INVALID_RELATIONSHIP_TYPE
}

enum ClientRelationship {
  INVALID_RELATIONSHIP_TYPE
  SPOUSE
  COHABITATING_PARTNER
  PSC
  DIRECTOR_TRUSTEE
  DIRECTOR_TRUSTEE_AND_PSC
  PARENT
  SIBLING
  CHILD
  OTHER
  DIRECTOR_TRUSTEE_OF
  PSC_OF
  DIRECTOR_TRUSTEE_AND_PSC_OF
  DUPLICATE
  DUPLICATE_OF
}

enum IncomeType {
  INVALID_INCOME_TYPE
  BASIC_SALARY
  COMMISSION
  OVERTIME
  BONUS
  OTHER
}

enum IncomeFrequency {
  INVALID_PAYMENT_FREQUENCY
  MONTHLY
  QUARTERLY
  ANNUAL
  WEEKLY
}

enum PaymentFrequency {
  INVALID_FREQUENCY
  WEEKLY
  MONTHLY
  QUARTERLY
  HALF_YEAR
  ANNUAL
}

enum UnearnedIncomeFrequency {
  INVALID_FREQUENCY
  WEEKLY
  MONTHLY
  QUARTERLY
  ANNUAL
}

enum PensionType {
  INDUSTRIAL_INJURIES_DISABLEMENT
  PRIVATE_RETIREMENT
  STATE_RETIREMENT
  WAR_DISABILITY
  WIDOWS
  OTHER
  INVALID_PENSION_TYPE
}

enum EmploymentType {
  FIXED_TERM
  PERMANENT
  TEMPORARY
  INVALID_EMPLOYMENT_TYPE
}

enum EmploymentBasis {
  FULL_TIME
  PART_TIME
  INVALID_EMPLOYED_BASIS
}

enum SelfEmployedBasis {
  INVALID_SELF_EMPLOYED_BASIS
  SOLE_TRADER
  PARTNERSHIP
  LLP
  PSC
  INVALID_SELF_EMPLOYMENT_TYPE
  LTD
}

enum BankAccountType {
  INVALID_TYPE
  CURRENT
  SAVINGS
}

enum TypeOfCredit {
  INVALID_CREDITOR_TYPE
  CREDITOR_TYPE_COMMUNICATIONS
  CREDITOR_TYPE_CREDIT_CREDIT
  CREDITOR_TYPE_HP
}

enum UnearnedIncomeType {
  INVALID_UNEARNED_INCOME_TYPE
  STATE_BENEFITS
  INVESTMENT
  DIVIDENDS
  RENTAL_INCOME
  OTHER
  UNIVERSAL_CREDITS
  STATE_PENSION
  PENSION_CREDITS
  DISABILITY_BENEFITS
  TAX_CREDITS
  CHILD_BENEFITS
  CHILD_MAINTENANCE
  FOSTERING
  TRUST_FUND
  ARMED_FORCES_INDEPENDENCE_PAYMENT
  ALIMONY_OR_SPOUSAL_MAINTENANCE
}

enum RepaymentFrequency {
  INVALID
  UNDEFINED
  WEEKLY
  MONTHLY
  QUARTERLY
  ANNUALLY
  ONCE
}

enum AccountStatus {
  ACCOUNT_STATUS_SETTLED
  ACCOUNT_STATUS_OPEN
  INVALID_ACCOUNT_STATUS
}

enum PaymentStatusSummary {
  INVALID_STATUS
  UP_TO_DATE
  ARREARS
  PAST_ARREARS
  DEFAULTED
  SETTLED
}

enum PaymentStatus {
  INVALID_STATUS
  UP_TO_DATE
  ONE_PAYMENT_IN_ARREARS
  TWO_PAYMENT_IN_ARREARS
  THREE_PAYMENT_IN_ARREARS
  FOUR_PAYMENT_IN_ARREARS
  FIVE_PAYMENT_IN_ARREARS
  SIX_PAYMENT_IN_ARREARS
  MORE_THAN_6_PAYMENT_IN_ARREARS
  SPECIAL_ARRANGEMENT
  SETTLED
  DEFAULT
  NO_STATUS_PROVIDED
}

enum Vulnerability {
  HEARING_VISUAL_IMPAIRMENT
  FOREIGN_LANGUAGE
  MENTAL_HEALTH
  ADDICTION
  STRESS
  DOMESTIC_ABUSE
  LONG_TERM_ILLNESS
  AGE
  MENTAL_CAPACITY
  INDEBTEDNESS
  LOW_INCOME
  CARER
  SKILLS
  CRIME
  MAJOR_LIFE_CHANGE
  OTHER
}

enum ExternalIdNames {
  INVALID_SOURCE_NAME
  ONFIDO
  ACRE_DATA_IMPORTER
  MORTGAGES_360
  INTELLIFLO
  PIPEDRIVE
  SMARTR_365
  THE_KEY
  XPLAN
  EQUIFAX
  GOCARDLESS_MANDATE
  ZAPIER
  MICROSOFT_GRAPH
  NYLAS
  ENBS
  LANDG
  COMENTIS
  ONE_MORTGAGE_SYSTEM
  CLEARSCORE
  CONNEX
}

type ClientBankAccount implements BankAccount {
  id: ID
  internalHash: String
  sort_code: String
  number: String
  names: [String]
  type: BankAccountType
  provider_code: String
  overdraft: String
  number_of_months_held: Int
}

type Deposit {
  id: ID
  internalHash: String
  amount: String
  type: DepositType
  # @TODO FRON-751 populate this with list of clients and documents
  # donors: [Client!]
  # documents: [DocumentEntity!]
  donors: [ClientVersion!]
  donorsCp: [ClientVersion!]
  documents: [String!]
  document_verifies: Boolean
  source: String
  deposit_for: [String]
}

type PaymentHistory {
  age_in_months: Int
  balance: String
  date: String
  payment: String
  payment_status: PaymentStatus
}

type ArrearsSummary {
  max_arrears_currency: String
  max_arrears_months: Int
  last_arrears_date: String
  arrears_cleared_date: String
}

type DefaultEvents {
  date_registered: String
  date_satisfied: String
  balance: String
}

type DebtJointApplicant {
  client_id: ID
  debt_id: ID
}

type DebtCalculatedValues {
  months_to_repay: Int
  total_interest: String
  end_date_discrepancy: Boolean
  months_to_repay_partial: Float
  average_monthly_repayment_amount: String
}

type Debt {
  id: ID
  internalHash: String
  commitment_type: DebtType
  borrower_type: BorrowerType
  amount_outstanding: String
  monthly_repayment_amount: String
  name_of_creditor: String
  final_repayment_date: String
  repaid_before_mortgage_commences: Boolean
  to_be_consolidated: Boolean
  credit_limit: String
  additional_details: String
  repaid_each_month: Boolean
  interest_rate: Int
  reference_account_no: String
  repayment_amount: String
  repayment_frequency: RepaymentFrequency
  start_date: String
  end_date: String
  term_in_months: Int
  payment_status_summary: PaymentStatusSummary
  last_update_date: String
  payment_history: [PaymentHistory]
  default_event: DefaultEvents
  arrears_summary: ArrearsSummary
  account_status: AccountStatus
  joint_applicant: DebtJointApplicant
  equifax_account_number: String
  equifax_promotional_rate_ind: Boolean
  revert_rate: Int
  revert_date: String
  estimated_interest_rates: Boolean
  early_repayment_percentage: Int
  early_repayment_amount: String
  average_monthly_repayment_amount: String
  amount_to_consolidate: String
  calculated_values: DebtCalculatedValues
}

type UnearnedIncome {
  id: ID
  internalHash: String
  type: UnearnedIncomeType
  amount: String
  frequency: UnearnedIncomeFrequency
  other_type: String
}

type GetClientResponseNextPage {
  client_details: Boolean
  show_protected: String
  show_summary: Boolean
  client_ids: [ID!]
  filter_organisation_id: ID
  filter_email_address: String
  filter_order_reverse: Boolean
  bookmark: String
  page_size: String
}

type GetClientResponseException {
  id: String!
}

type GetClientResponse {
  clients: [ClientVersion!]
  next_page: GetClientResponseNextPage
  exception: GetClientResponseException
}

type ClientVersion implements VersionedObject {
  id: ID!
  client_id: ID
  created_at: String
  version: Int
  version_created_at: String
  details: Client!
  versions: [VersionWithChangedFields]
}

enum ClientContactType {
  INVALID_CONTACT_TYPE
  EMAIL
  OTHER
  SMS
}

type ContactHistory {
  contact_type: ClientContactType
  created_at: String
  identifier: String
  third_party_id: String
  clicked_at: String
  clicks: Int
  opened_at: String
  delivered: Boolean
}

type Client {
  id: ID!
  client_id: ID
  version: Int
  face_to_face: Boolean
  title: Title
  first_name: String
  middle_name: String
  prev_first_name: String
  prev_surname: String
  name_change_reason: String
  maiden_or_previous_name: String
  date_of_name_change: String
  last_name: String
  previous_name: PreviousClientName
  previous_names: [PreviousNames!]
  date_of_birth: String
  country_of_birth: String
  client_type: [ClientType!]
  gender: Gender
  marital_status: MaritalStatus

  #  Contact details
  contact_details_email: String
  contact_details_home_phone: String
  contact_details_work_phone: String
  contact_details_mobile_phone: String
  contact_history: [ContactHistory]

  # Net income
  net_monthly_income: String

  # Outgoings
  outgoings_maintenance_payments: String
  outgoings_water: String
  outgoings_communications: String
  outgoings_mortgage_rent: String
  outgoings_insurance: String
  outgoings_investments: String
  outgoings_council_tax: String
  outgoings_food: String
  outgoings_clothing: String
  outgoings_other_living_costs: String
  outgoings_entertainment: String
  outgoings_holidays: String
  outgoings_sports: String
  outgoings_pension: String
  outgoings_car_costs: String
  outgoings_other_transport_costs: String
  outgoings_child_care: String
  outgoings_fuel: String
  outgoings_ground_rent_service_charge_shared_equity_rent: String
  outgoings_television_license: String
  outgoings_household_repairs: String
  outgoings_additional_details: String
  outgoings_material_change_details: String

  # Wills
  has_will: Boolean
  has_reviewed_will_in_last_five_years: Boolean

  # Credit history
  credit_history_has_ever_surrendered_a_property_or_been_repossessed: Boolean
  credit_history_has_ever_surrendered_a_property_or_been_repossessed_details: String
  credit_history_adverse_details_has_client_had_ccj: Boolean
  credit_history_county_court_judgements: [ClientDetailsCountyCourtJudgement!]
  credit_history_adverse_details_has_client_declared_bankruptcy: Boolean
  credit_history_bankruptcy_events: [ClientDetailsBankruptcyEvent!]
  credit_history_adverse_details_has_client_had_default: Boolean
  credit_history_default_events: [ClientDetailsDefaultEvent!]
  credit_history_adverse_details_has_client_had_debt_management_plan: Boolean
  credit_history_debt_management_plans: [ClientDetailsDebtManagementPlan!]
  credit_history_adverse_details_has_arrears: Boolean
  credit_history_arrears: [ClientDetailsArrears!]
  credit_history_adverse_details_has_client_had_iva: Boolean
  credit_history_individual_voluntary_arrangements: [ClientDetailsIndividualVoluntaryArrangement!]
  credit_history_adverse_details_has_client_missed_payments: Boolean
  credit_history_adverse_details_number_of_missed_payments: Int
  credit_history_adverse_details_has_missed_payments_cleared: Boolean
  credit_history_adverse_details_missed_any_payments_on_commitments_details: String
  credit_history_have_you_ever_had_an_application_denied_or_refused: Boolean
  mortgage_application_refused: [ClientDetailsCreditRefusal!]

  # email_address: String
  addresses: [ClientAddress!]
  retirement_age: Int
  residency_status: ResidencyStatus
  is_politically_exposed: Boolean
  applicants_are_high_tax_rate_payers: Boolean
  is_vulnerable: Boolean
  nationalities: [String!]
  employment_status: EmploymentStatus
  amount_of_savings: String
  dependants: [Dependant!]
  national_insurance_number: String
  relationships: [Relationship!]
  relatedClients(relationshipTypes: [ClientRelationship!], fromClientPortal: Boolean): [ClientVersion!]
  accounts: [ClientBankAccount!]
  deposits: [Deposit!]
  debts: [Debt!]
  disposable_income: String
  agreed_budget: String
  agreed_monthly_protection_budget: String
  outgoings: ClientOutgoings
  outgoings_change_details: String
  income_and_employment: [ClientIncome!]
  anticipated_retirement_income: [ClientIncome!]
  cases: [CaseVersion!]
  mortgages: [Mortgage!]
  properties(includeDirectorsProperties: Boolean): [PropertyVersion!]
  propertiesCp: [PropertyVersion!]
  id_verification_status: ClientIdVerificationStatus
  client_originator: ID
  is_natural_person: Boolean
  limited_company_registration_number: String
  organisation_name: String
  non_natural: NonNatural
  organisation_sic: ClientOrganisationSIC
  non_natural_organisation_type: ClientOrganisationType
  unearned_income: [UnearnedIncome!]
  anticipated_retirement_unearned_income: [UnearnedIncome!]
  total_guaranteed_annual: String
  total_additional_annual: String

  # Client portal
  clientUserIdd: DocumentEntity

  # Credit
  credit_idd: DocumentEntity
  opt_out_credit_refresh: Boolean

  # Calculated values
  calculated_values: CalculatedClientValues

  credit_report_retrievals: [CreditReportRetrievals]

  # Documents
  miDocuments(filters: MIDocumentSummaryFiltersInput): MIDocumentSummaryResponse
  document_categories(id: ID!, version: Int!, filters: MIDocumentTotalsFiltersInput): [MIDocumentCategory!]
  document_totals(filters: MIDocumentTotalsFiltersInput): [MIDocumentTotal!]

  # Vulnerabilities
  vulnerabilities: [Vulnerability!]
  additional_vulnerabilities_details: String
  vulnerability_details: [VulnerabilityTag!]
  vulnerability_assessments: [VulnerabilityAssessment!]

  # IO data
  external: [ExternalClient]
  client_ext_ids: [ClientExtIds]

  # Health
  smoker_status: Boolean
  client_weight: Int
  client_height: Int
  display_weight_unit: MeasurementUnit
  display_height_unit: MeasurementUnit
  health_information: ClientHealthInfo

  # Risk report
  risk_reports: [RiskReport!]
}

type VulnerabilityTag {
  id: ID!
  type: Vulnerability
  source: VulnerabilitySource
  source_id: String
  actions: [VulnerabilityAction!]
  created_at: String
  modified_at: String
}

type VulnerabilityAction {
  id: ID!
  action: String
  response: String
}

enum VulnerabilitySource {
  SOURCE_UNSET
  SOURCE_USER
  SOURCE_COMPLIANCE_USER
  SOURCE_CLIENT
  SOURCE_ACRE_AI
  SOURCE_COMENTIS
}

type VulnerabilityAssessment {
  id: ID!
  provider: VulnerabilityProvider!
  template_id: String
  complete: Boolean
  created_at: String
  modified_at: String
}

type RiskReport {
  created_at: String
  document_id: String
  client_hash: String
  term: Int
  results: ClientRiskReportResults
}

type ClientRiskReportResults {
  mortality_over_term: Int
  loss_of_income_over_term: Int
  morbidity_or_mortality_over_term: Int
  morbidity_or_mortality_or_loss_of_income_over_term: Int
}

input ClientEntityVersionInput {
  client_id: String!
  version: Int!
  show_protected: Boolean
}

type ClientEntityVersion {
  client_id: String!
  version: Int!
}

type CreditReportRetrievals {
  raw_result_reference: String
  raw_result_signature: String
  retrieval_time: String
  pdf_document_id: String
  credit_search_consent_id: String
}

type NonNatural {
  number_of_employees: Int
}

type CalculatedClientValues {
  current_eidv_hash: String
  age: Int
  risk_details_hash: String
}

type ClientDetailsBankruptcyEvent {
  id: ID
  internalHash: String
  bankruptcy_date: String
  discharge_date: String
  court: String
}

type ClientDetailsDefaultEvent {
  date_registered: String
  date_satisfied: String
  amount: String
  creditor_name: String
}

type ClientDetailsCountyCourtJudgement {
  id: ID
  internalHash: String
  date_registered: String
  date_satisfied: String
  amount: String
  creditor_name: String
}

enum MortgageApplicationRefusedApplicationRefusedType {
  INVALID_APPLICATION_REFUSED_TYPE
  CREDIT_CARD
  HIRE_PURCHASE_AGREEMENT
  MORTGAGE
  UNSECURED_LOAN
}

type ClientDetailsCreditRefusal {
  id: ID
  internalHash: String
  application_refused_date: String
  application_refused_creditor: String
  application_refused_type: MortgageApplicationRefusedApplicationRefusedType
  application_refused_details: String
}

type ClientDetailsDebtManagementPlan {
  id: ID
  internalHash: String
  date_registered: String
  date_completed: String
  monthly_payment_amount: String
  satisfactory_conduct: Boolean
}

type ClientDetailsArrears {
  has_cleared: Boolean
  amount: String
  latest_date_account_three_or_more_months_in_arrears: String
  max_number_of_monthly_payments_in_arrears: Int
  type_of_credit: TypeOfCredit
  creditor_name: String
  date_of_arrear: String
  date_cleared: String
}

type ClientDetailsIndividualVoluntaryArrangement {
  id: ID
  internalHash: String
  date_registered: String
  date_completed: String
  monthly_payment_amount: String
  satisfactory_conduct: Boolean
}

type PreviousClientName {
  previous_name: String
  date_of_name_change: String
}

type PreviousNames {
  id: ID
  internalHash: String
  previous_first_name: String
  previous_surname: String
  date_of_name_change: String
  reason_for_name_change: String
}

type Dependant {
  id: ID
  internalHash: String
  date_of_birth: String
  relationship: DependantRelationship!
  relationship_other: String
  name: String
  title: Title
  dependency_end_date: String
}

type Relationship {
  relationship_reference: String
  relationship_type: ClientRelationship
  relationship_data_access_permitted: Boolean
}

type ClientOutgoings {
  committed_expenditure: String
  discretionary_expenditure: String
  agreed_budget: String
  agreed_monthly_protection_budget: String
  monthly_repayment_amount: String
  net_monthly_income: String
  outgoings_maintenance_payments: String
  outgoings_water: String
  outgoings_communications: String
  outgoings_mortgage_rent: String
  outgoings_insurance: String
  outgoings_investments: String
  outgoings_council_tax: String
  outgoings_food: String
  outgoings_clothing: String
  outgoings_other_living_costs: String
  outgoings_entertainment: String
  outgoings_holidays: String
  outgoings_sports: String
  outgoings_pension: String
  outgoings_car_costs: String
  outgoings_other_transport_costs: String
  outgoings_child_care: String
  outgoings_fuel: String
  outgoings_ground_rent_service_charge_shared_equity_rent: String
  outgoings_television_license: String
  outgoings_household_repairs: String
  outgoings_additional_details: String
  outgoings_material_change_details: String
  sharedBudget: ClientOutgoings
}

input ClientOutgoingsInput {
  committed_expenditure: String
  discretionary_expenditure: String
  agreed_budget: String
  agreed_monthly_protection_budget: String
  shared_net_monthly_income: String
  net_monthly_income: String
  monthly_repayment_amount: String
  outgoings_maintenance_payments: String
  outgoings_water: String
  outgoings_communications: String
  outgoings_mortgage_rent: String
  outgoings_insurance: String
  outgoings_investments: String
  outgoings_council_tax: String
  outgoings_food: String
  outgoings_clothing: String
  outgoings_other_living_costs: String
  outgoings_entertainment: String
  outgoings_holidays: String
  outgoings_sports: String
  outgoings_pension: String
  outgoings_car_costs: String
  outgoings_other_transport_costs: String
  outgoings_child_care: String
  outgoings_fuel: String
  outgoings_ground_rent_service_charge_shared_equity_rent: String
  outgoings_television_license: String
  outgoings_household_repairs: String
  outgoings_additional_details: String
  outgoings_material_change_details: String
}

enum SickPayType {
  STATUTORY_SICK_PAY
  EMPLOYER_SICK_PAY
  EXTERNAL_POLICY
  NONE
}

type ClientIncomeSickPay {
  sick_pay_type: SickPayType
  sick_pay_amount_monthly: String
  sick_pay_period_months: Int
  sick_pay_details: String
  allowances: [SickPayAllowance]
}

type SickPayAllowance {
  percentage: Int
  duration: Int
  duration_unit: SickPayDurationUnit
}

type ClientIncome {
  id: ID
  internalHash: String
  employment: ClientEmploymentIncome
  self_employment: ClientSelfEmploymentIncome
  date_of_retirement: String
  is_retired: Boolean
  pension: ClientPensionIncome
  foreign_currency_amount: String
  payment_frequency: PaymentFrequency
  earned_income_guaranteed: String
  earned_income_regular: String
  earned_income_irregular: String
  sick_pay: ClientIncomeSickPay
  history: [String]
  documents: [String]
  pay_rise_expected: Boolean
  pay_rise_expected_details: String
  pay_rise_new_salary: String
  pay_rise_due_in_num_months: Int
  income_likely_to_reduce_in_future: Boolean
  income_likely_to_reduce_in_future_description: String
  standardised_occupation_code: String
  abi_industry: String
  occupation_title: String
  is_current: Boolean
  monthly_net_income: String
}

type PayRise {
  expected: Boolean
  details: String
  due_num_months: Int
  new_salary: Int
}

type ClientEmploymentIncome {
  employment_type: EmploymentType
  employment_basis: EmploymentBasis
  employment_start_date: String
  employment_end_date: String
  no_other_recent_employment: Boolean
  employer_name: String
  will_contract_be_renewed: Boolean
  contract_renewal_details: String
  on_probation: Boolean
  probation_details: String
  shareholding_percentage: Int
  employer_address: LutherAddress
  employer_work_phone: String
  employer_mobile_phone: String
  employer_email: String
  employer_alternate_email: String
  incomes: [ClientEmploymentIncomeDetails!]
  total_guaranteed_annual: String
  total_additional_annual: String
}

type ClientSelfEmploymentIncome {
  has_accountant: Boolean
  self_employed_basis: SelfEmployedBasis
  shareholding_percentage: Int
  business_start_date: String
  employed_for_tax_purposes: Boolean
  your_role_in_business_end_date: String
  financial_year_end_date: String
  connected_with_business_since_date: String
  nature_of_business: String
  limited_company_registration_number: String
  business_name: String
  accountant_qualifications: [AccountantQualification]
  no_of_years_accounts_available: Int
  net_profits_last_year: String
  net_profits_two_years_ago: String
  net_profits_three_years_ago: String
  business_address: LutherAddress
  business_work_phone: String
  business_mobile_phone: String
  business_email: String
  business_alternate_email: String
  accountant_name: String
  accountant_address: LutherAddress
  accountant_work_phone: String
  accountant_mobile_phone: String
  accountant_email: String
  accountant_alternate_email: String
  taxable_income_last_year: String
  taxable_income_two_years_ago: String
  taxable_income_three_years_ago: String
  percentage_share_last_year: Int
  percentage_share_two_years_ago: Int
  percentage_share_three_years_ago: Int
  tax_year_last_year: Float
  tax_year_two_years_ago: Float
  tax_year_three_years_ago: Float
  tax_deductions: String
  national_insurance_deductions: String
  salary_income_last_year: String
  salary_income_two_years_ago: String
  salary_income_three_years_ago: String
  dividend_income_last_year: String
  dividend_income_two_years_ago: String
  dividend_income_three_years_ago: String
  construction_industry_scheme: Boolean
}

type PensionProvider {
  name: String
  phone_number: String
  address: Address
}

type ClientPensionIncome {
  provider_phone_number: String
  pension_type: PensionType
  reference_number: String
  provider_name: String
  provider_address: LutherAddress
  gross_annual_pension_income: String
}

type ClientIdVerificationStatus {
  eidvs: [ClientEidv!]
}

type ClientEidv {
  eidv_check_completed: String!
  eidv_check_id: String!
  eidv_check_requested: String!
  eidv_check_status: EidvResult!
  eidv_extended_status: String
  eidv_signature: String
  eidv_sanction: Boolean
  eidv_pep: Boolean
  eidv_special_interest_person: Boolean
  eidv_close_associate: Boolean
  eidv_client_details_hash: String
  additional_eidv_information: [AdditionalEidvInformation!]
}

type AdditionalEidvInformation {
  description: String
  data: String
}

enum EidvResult {
  CLEAR
  CONSIDER
  CIFAS_MATCH
  UNIDENTIFIED
  INVALID_EIDV_RESULT
  MATCHES_MORTALITY_LISTS
  SERVICE_UNAVAILABLE
  UNSUPPORTED
}

type MarketingPreferences {
  org_id: String
  contact_by_phone: Boolean
  contact_by_email: Boolean
  contact_by_text: Boolean
  contact_by_mail: Boolean
  third_party_contact_by_phone: Boolean
  third_party_contact_by_email: Boolean
  third_party_contact_by_text: Boolean
  third_party_contact_by_mail: Boolean
}

input MarketingPreferencesInput {
  contact_by_phone: Boolean
  contact_by_email: Boolean
  contact_by_text: Boolean
  contact_by_mail: Boolean
  third_party_contact_by_phone: Boolean
  third_party_contact_by_email: Boolean
  third_party_contact_by_text: Boolean
  third_party_contact_by_mail: Boolean
}

type ClientOrganisationRelationship {
  org_id: String
  client_id: String
  relationship_type: ClientOrganisationRelationshipType
}

type ExternalClientSource {
  id: String
  name: ExternalIdNames
}

type ExternalClient {
  id: String
  source: ExternalClientSource
}

type ClientExtIds {
  id: String
  name: ExternalIdNames
}

input ClientOrganisationRelationshipInput {
  org_id: String
  client_id: String
  relationship_type: ClientOrganisationRelationshipType
}

enum ClientOrganisationRelationshipType {
  INVALID_RELATIONSHIP
  ADVICE
  INTRODUCER
  PRODUCT_PROVIDER
  REFERRED_ADVICE
}

input ClientContactDetailsInput {
  contact_details_home_phone: String
  contact_details_work_phone: String
  contact_details_mobile_phone: String
  contact_details_email: String
  addresses: [ClientAddressInput]
}

input UnearnedIncomeInput {
  id: ID
  internalHash: String
  type: UnearnedIncomeType
  amount: String
  frequency: UnearnedIncomeFrequency
  other_type: String
}

input ClientInput {
  id: ID
  title: Title
  first_name: String
  middle_name: String
  last_name: String
  prev_first_name: String
  prev_surname: String
  name_change_reason: String
  maiden_or_previous_name: String
  date_of_name_change: String
  previous_names: [PreviousNamesInput!]
  date_of_birth: String
  marital_status: MaritalStatus
  gender: Gender
  contact_details_email: String
  contact_details_work_phone: String
  contact_details_home_phone: String
  contact_details_mobile_phone: String
  client_type: [ClientType!]
  income_and_employment: [ClientIncomeInput!]
  anticipated_retirement_income: [ClientIncomeInput!]
  deposits: [DepositInput!]
  dependants: [DependantInput!]
  relationships: [RelationshipInput!]
  debts: [DebtInput!]
  addresses: [ClientAddressInput!]
  accounts: [ClientBankAccountInput!]
  employment_status: EmploymentStatus
  amount_of_savings: String
  disposable_income: String
  agreed_budget: String
  agreed_monthly_protection_budget: String
  client_originator: ID
  non_natural: NonNaturalInput
  # Wills
  has_will: Boolean
  has_reviewed_will_in_last_five_years: Boolean

  # Net income
  net_monthly_income: String

  # Outgoings
  outgoings_maintenance_payments: String
  outgoings_water: String
  outgoings_communications: String
  outgoings_mortgage_rent: String
  outgoings_insurance: String
  outgoings_investments: String
  outgoings_council_tax: String
  outgoings_food: String
  outgoings_clothing: String
  outgoings_other_living_costs: String
  outgoings_entertainment: String
  outgoings_holidays: String
  outgoings_sports: String
  outgoings_pension: String
  outgoings_car_costs: String
  outgoings_other_transport_costs: String
  outgoings_child_care: String
  outgoings_fuel: String
  outgoings_ground_rent_service_charge_shared_equity_rent: String
  outgoings_television_license: String
  outgoings_household_repairs: String
  outgoings_additional_details: String
  outgoings_material_change_details: String

  sharedBudget: ClientOutgoingsInput
  shared_budget: Boolean
  committed_expenditure: String
  discretionary_expenditure: String
  country_of_birth: String
  nationalities: [String!]
  retirement_age: Int
  applicants_are_high_tax_rate_payers: Boolean
  is_vulnerable: Boolean
  is_natural_person: Boolean
  limited_company_registration_number: String
  organisation_name: String
  organisation_sic: ClientOrganisationSIC
  non_natural_organisation_type: ClientOrganisationType
  national_insurance_number: String
  credit_history_has_ever_surrendered_a_property_or_been_repossessed: Boolean
  credit_history_has_ever_surrendered_a_property_or_been_repossessed_details: String
  credit_history_adverse_details_has_client_had_ccj: Boolean
  credit_history_county_court_judgements: [ClientDetailsCountyCourtJudgementInput!]
  credit_history_adverse_details_has_client_declared_bankruptcy: Boolean
  credit_history_bankruptcy_events: [ClientDetailsBankruptcyEventInput!]
  credit_history_adverse_details_has_client_had_default: Boolean
  credit_history_default_events: [ClientDetailsDefaultEventInput!]
  credit_history_adverse_details_has_client_had_debt_management_plan: Boolean
  credit_history_debt_management_plans: [ClientDetailsDebtManagementPlanInput!]
  credit_history_adverse_details_has_arrears: Boolean
  credit_history_arrears: [ClientDetailsArrearsInput!]
  credit_history_adverse_details_has_client_had_iva: Boolean
  credit_history_individual_voluntary_arrangements: [ClientDetailsIndividualVoluntaryArrangementInput!]
  credit_history_adverse_details_has_client_missed_payments: Boolean
  credit_history_adverse_details_number_of_missed_payments: Int
  credit_history_adverse_details_has_missed_payments_cleared: Boolean
  credit_history_adverse_details_missed_any_payments_on_commitments_details: String
  credit_history_have_you_ever_had_an_application_denied_or_refused: Boolean
  mortgage_application_refused: [ClientDetailsCreditRefusalInput!]
  unearned_income: [UnearnedIncomeInput!]
  anticipated_retirement_unearned_income: [UnearnedIncomeInput!]

  # Protection
  total_guaranteed_annual: String
  total_additional_annual: String

  credit_report_retrievals: [CreditReportRetrievalsInput]

  vulnerabilities: [Vulnerability!]
  vulnerability_details: [VulnerabilityTagInput!]
  additional_vulnerabilities_details: String
  external: [ExternalClientInput]

  opt_out_credit_refresh: Boolean

  # Health
  smoker_status: Boolean
  client_weight: Int
  client_height: Int
  display_weight_unit: MeasurementUnit
  display_height_unit: MeasurementUnit
  health_information: ClientHealthInfoInput
}

input VulnerabilityTagInput {
  id: ID
  type: Vulnerability
  source: VulnerabilitySource
  source_id: String
  actions: [VulnerabilityActionInput!]
  created_at: String
  modified_at: String
}

input VulnerabilityActionInput {
  id: ID
  action: String
  response: String
}

input ExternalClientSourceInput {
  id: String
  name: ExternalIdNames
}

input ExternalClientInput {
  id: String
  source: ExternalClientSourceInput
}

input ClientExtIdsInput {
  id: String
  name: ExternalIdNames
}

input CreditReportRetrievalsInput {
  raw_result_reference: String
  raw_result_signature: String
  retrieval_time: String
  pdf_document_id: String
  credit_search_consent_id: String
}

type ClientEmploymentIncomeDetails {
  is_guaranteed: Boolean
  income_amount: String
  income_currency: String
  income_frequency: IncomeFrequency
  income_type: IncomeType
  income_other_type: String
  income_history: [Int!]
  income_history_last_date: String
}

input NonNaturalInput {
  number_of_employees: Int
}

input ClientDetailsBankruptcyEventInput {
  id: ID
  internalHash: String
  bankruptcy_date: String
  discharge_date: String
  court: String
}

input ClientDetailsDefaultEventInput {
  id: ID
  date_registered: String
  date_satisfied: String
  amount: String
  creditor_name: String
}

input ClientDetailsCountyCourtJudgementInput {
  id: ID
  internalHash: String
  date_registered: String
  date_satisfied: String
  amount: String
  creditor_name: String
}

input ClientDetailsCreditRefusalInput {
  id: ID
  internalHash: String
  application_refused_date: String
  application_refused_creditor: String
  application_refused_type: MortgageApplicationRefusedApplicationRefusedType
  application_refused_details: String
}

input ClientDetailsDebtManagementPlanInput {
  id: ID
  internalHash: String
  date_registered: String
  date_completed: String
  monthly_payment_amount: String
  satisfactory_conduct: Boolean
}

input ClientDetailsArrearsInput {
  id: ID
  has_cleared: Boolean
  amount: String
  latest_date_account_three_or_more_months_in_arrears: String
  max_number_of_monthly_payments_in_arrears: Int
  type_of_credit: TypeOfCredit
  creditor_name: String
  date_of_arrear: String
  date_cleared: String
}

input ClientDetailsIndividualVoluntaryArrangementInput {
  id: ID
  internalHash: String
  date_registered: String
  date_completed: String
  monthly_payment_amount: String
  satisfactory_conduct: Boolean
}
input ClientIncomeSickPayInput {
  sick_pay_type: SickPayType
  sick_pay_amount_monthly: String
  sick_pay_period_months: Int
  sick_pay_details: String
  allowances: [SickPayAllowanceInput]
}

input ClientIncomeInput {
  id: ID
  internalHash: String
  employment: ClientEmploymentInput
  self_employment: ClientSelfEmploymentInput
  date_of_retirement: String
  is_retired: Boolean
  pension: ClientPensionInput
  earned_income_guaranteed: String
  earned_income_regular: String
  earned_income_irregular: String
  payment_frequency: PaymentFrequency
  foreign_currency_amount: String
  pay_rise_expected: Boolean
  pay_rise_expected_details: String
  pay_rise_new_salary: String
  pay_rise_due_in_num_months: Int
  income_likely_to_reduce_in_future: Boolean
  income_likely_to_reduce_in_future_description: String
  standardised_occupation_code: String
  abi_industry: String
  occupation_title: String
  monthly_net_income: String
  is_current: Boolean
  sick_pay: ClientIncomeSickPayInput
}

input ClientPensionInput {
  provider_phone_number: String
  pension_type: PensionType
  reference_number: String
  provider_name: String
  provider_address: LutherAddressInput
  gross_annual_pension_income: String
}

input SickPayAllowanceInput {
  percentage: Int
  duration: Int
  duration_unit: SickPayDurationUnit
}

enum SickPayDurationUnit {
  INVALID
  UNDEFINED
  WEEKLY
  MONTHLY
  QUARTERLY
  ANNUALLY
  ONCE
}

input ClientBankAccountInput {
  id: ID
  internalHash: String
  type: BankAccountType
  number: String
  sort_code: String
  number_of_months_held: Int
}

input ClientEmploymentInput {
  employment_type: EmploymentType
  employment_basis: EmploymentBasis
  employment_start_date: String
  employment_end_date: String
  no_other_recent_employment: Boolean
  employer_name: String
  will_contract_be_renewed: Boolean
  contract_renewal_details: String
  on_probation: Boolean
  probation_details: String
  shareholding_percentage: Int
  employer_address: LutherAddressInput
  employer_work_phone: String
  employer_mobile_phone: String
  employer_email: String
  employer_alternate_email: String
  incomes: [ClientEmploymentIncomeDetailsInput!]
  total_guaranteed_annual: String
  total_additional_annual: String
}

input ClientEmploymentIncomeDetailsInput {
  is_guaranteed: Boolean
  income_amount: String
  income_currency: String
  income_frequency: IncomeFrequency
  income_type: IncomeType
  income_other_type: String
  income_history: [Int!]
  income_history_last_date: String
}

input ClientSelfEmploymentInput {
  accountant_address: LutherAddressInput
  accountant_email: String
  accountant_name: String
  accountant_work_phone: String
  business_address: LutherAddressInput
  business_name: String
  business_start_date: String
  employed_for_tax_purposes: Boolean
  has_accountant: Boolean
  national_insurance_deductions: String
  net_profits_last_year: String
  net_profits_three_years_ago: String
  net_profits_two_years_ago: String
  no_of_years_accounts_available: Int
  percentage_share_last_year: Int
  percentage_share_three_years_ago: Int
  percentage_share_two_years_ago: Int
  self_employed_basis: SelfEmployedBasis
  shareholding_percentage: Int
  tax_deductions: String
  taxable_income_last_year: String
  taxable_income_three_years_ago: String
  taxable_income_two_years_ago: String
  your_role_in_business_end_date: String
  connected_with_business_since_date: String
  salary_income_last_year: String
  salary_income_two_years_ago: String
  salary_income_three_years_ago: String
  dividend_income_last_year: String
  dividend_income_two_years_ago: String
  dividend_income_three_years_ago: String
  construction_industry_scheme: Boolean
  financial_year_end_date: String
}

input DepositInput {
  id: ID
  internalHash: String
  amount: String
  type: DepositType
  # @TODO FRON-751 populate this with list of clients and documents
  # donors: [ClientInput]
  # documents: [DocumentInput]
  donors: [ID!]
  documents: [String!]
  document_verifies: Boolean
  source: String
  deposit_for: [String]
}

input DependantInput {
  id: ID
  internalHash: String
  date_of_birth: String
  relationship: DependantRelationship!
  relationship_other: String
  name: String
  title: Title
  dependency_end_date: String
}

input RelationshipInput {
  relationship_reference: String
  relationship_type: ClientRelationship
  relationship_data_access_permitted: Boolean
}

input PaymentHistoryInput {
  age_in_months: Int
  balance: String
  date: String
  payment: String
  payment_status: PaymentStatus
}

input ArrearsSummaryInput {
  max_arrears_currency: String
  max_arrears_months: Int
  last_arrears_date: String
  arrears_cleared_date: String
}

input DefaultEventsInput {
  date_registered: String
  date_satisfied: String
  balance: String
}

input DebtJointApplicantInput {
  client_id: ID
  debt_id: ID
}

input DebtInput {
  id: ID
  internalHash: String
  commitment_type: DebtType
  borrower_type: BorrowerType
  amount_outstanding: String
  monthly_repayment_amount: String
  name_of_creditor: String
  final_repayment_date: String
  repaid_before_mortgage_commences: Boolean
  to_be_consolidated: Boolean
  credit_limit: String
  additional_details: String
  repaid_each_month: Boolean
  interest_rate: Int
  reference_account_no: String
  repayment_amount: String
  repayment_frequency: RepaymentFrequency
  start_date: String
  end_date: String
  term_in_months: Int
  payment_status_summary: PaymentStatusSummary
  last_update_date: String
  payment_history: [PaymentHistoryInput]
  default_event: DefaultEventsInput
  arrears_summary: ArrearsSummaryInput
  account_status: AccountStatus
  joint_applicant: DebtJointApplicantInput
  equifax_account_number: String
  equifax_promotional_rate_ind: Boolean
  revert_rate: Int
  revert_date: String
  estimated_interest_rates: Boolean
  early_repayment_percentage: Int
  early_repayment_amount: String
  average_monthly_repayment_amount: String
  amount_to_consolidate: String
}

input PreviousNamesInput {
  previous_first_name: String
  previous_surname: String
  date_of_name_change: String
  reason_for_name_change: String
  id: ID
  internalHash: String
}

type ClientVerification {
  verification_collections: [VerificationCollection!]
}

enum ClientOrganisationType {
  INVALID_NON_NATURAL_ORGANISATION_TYPE
  NON_NATURAL_ORGANISATION_TYPE_OTHER
  LIMITED
  PLC
  LLP
  CHARTERED_BODY
  INCORPORATED_TRUST
  GOVERNMENTAL_BODY
}

enum ClientOrganisationSIC {
  INVALID_SIC2007
  SIC2007_01110
  SIC2007_01120
  SIC2007_01130
  SIC2007_01140
  SIC2007_01150
  SIC2007_01160
  SIC2007_01190
  SIC2007_01210
  SIC2007_01220
  SIC2007_01230
  SIC2007_01240
  SIC2007_01250
  SIC2007_01260
  SIC2007_01270
  SIC2007_01280
  SIC2007_01290
  SIC2007_01300
  SIC2007_01410
  SIC2007_01420
  SIC2007_01430
  SIC2007_01440
  SIC2007_01450
  SIC2007_01460
  SIC2007_01470
  SIC2007_01490
  SIC2007_01500
  SIC2007_01610
  SIC2007_01621
  SIC2007_01629
  SIC2007_01630
  SIC2007_01640
  SIC2007_01700
  SIC2007_02100
  SIC2007_02200
  SIC2007_02300
  SIC2007_02400
  SIC2007_03110
  SIC2007_03120
  SIC2007_03210
  SIC2007_03220
  SIC2007_05101
  SIC2007_05102
  SIC2007_05200
  SIC2007_06100
  SIC2007_06200
  SIC2007_07100
  SIC2007_07210
  SIC2007_07290
  SIC2007_08110
  SIC2007_08120
  SIC2007_08910
  SIC2007_08920
  SIC2007_08930
  SIC2007_08990
  SIC2007_09100
  SIC2007_09900
  SIC2007_10110
  SIC2007_10120
  SIC2007_10130
  SIC2007_10200
  SIC2007_10310
  SIC2007_10320
  SIC2007_10390
  SIC2007_10410
  SIC2007_10420
  SIC2007_10511
  SIC2007_10512
  SIC2007_10519
  SIC2007_10520
  SIC2007_10611
  SIC2007_10612
  SIC2007_10620
  SIC2007_10710
  SIC2007_10720
  SIC2007_10730
  SIC2007_10810
  SIC2007_10821
  SIC2007_10822
  SIC2007_10831
  SIC2007_10832
  SIC2007_10840
  SIC2007_10850
  SIC2007_10860
  SIC2007_10890
  SIC2007_10910
  SIC2007_10920
  SIC2007_11010
  SIC2007_11020
  SIC2007_11030
  SIC2007_11040
  SIC2007_11050
  SIC2007_11060
  SIC2007_11070
  SIC2007_12000
  SIC2007_13100
  SIC2007_13200
  SIC2007_13300
  SIC2007_13910
  SIC2007_13921
  SIC2007_13922
  SIC2007_13923
  SIC2007_13931
  SIC2007_13939
  SIC2007_13940
  SIC2007_13950
  SIC2007_13960
  SIC2007_13990
  SIC2007_14110
  SIC2007_14120
  SIC2007_14131
  SIC2007_14132
  SIC2007_14141
  SIC2007_14142
  SIC2007_14190
  SIC2007_14200
  SIC2007_14310
  SIC2007_14390
  SIC2007_15110
  SIC2007_15120
  SIC2007_15200
  SIC2007_16100
  SIC2007_16210
  SIC2007_16220
  SIC2007_16230
  SIC2007_16240
  SIC2007_16290
  SIC2007_17110
  SIC2007_17120
  SIC2007_17211
  SIC2007_17219
  SIC2007_17220
  SIC2007_17230
  SIC2007_17240
  SIC2007_17290
  SIC2007_18110
  SIC2007_18121
  SIC2007_18129
  SIC2007_18130
  SIC2007_18140
  SIC2007_18201
  SIC2007_18202
  SIC2007_18203
  SIC2007_19100
  SIC2007_19201
  SIC2007_19209
  SIC2007_20110
  SIC2007_20120
  SIC2007_20130
  SIC2007_20140
  SIC2007_20150
  SIC2007_20160
  SIC2007_20170
  SIC2007_20200
  SIC2007_20301
  SIC2007_20302
  SIC2007_20411
  SIC2007_20412
  SIC2007_20420
  SIC2007_20510
  SIC2007_20520
  SIC2007_20530
  SIC2007_20590
  SIC2007_20600
  SIC2007_21100
  SIC2007_21200
  SIC2007_22110
  SIC2007_22190
  SIC2007_22210
  SIC2007_22220
  SIC2007_22230
  SIC2007_22290
  SIC2007_23110
  SIC2007_23120
  SIC2007_23130
  SIC2007_23140
  SIC2007_23190
  SIC2007_23200
  SIC2007_23310
  SIC2007_23320
  SIC2007_23410
  SIC2007_23420
  SIC2007_23430
  SIC2007_23440
  SIC2007_23490
  SIC2007_23510
  SIC2007_23520
  SIC2007_23610
  SIC2007_23620
  SIC2007_23630
  SIC2007_23640
  SIC2007_23650
  SIC2007_23690
  SIC2007_23700
  SIC2007_23910
  SIC2007_23990
  SIC2007_24100
  SIC2007_24200
  SIC2007_24310
  SIC2007_24320
  SIC2007_24330
  SIC2007_24340
  SIC2007_24410
  SIC2007_24420
  SIC2007_24430
  SIC2007_24440
  SIC2007_24450
  SIC2007_24460
  SIC2007_24510
  SIC2007_24520
  SIC2007_24530
  SIC2007_24540
  SIC2007_25110
  SIC2007_25120
  SIC2007_25210
  SIC2007_25290
  SIC2007_25300
  SIC2007_25400
  SIC2007_25500
  SIC2007_25610
  SIC2007_25620
  SIC2007_25710
  SIC2007_25720
  SIC2007_25730
  SIC2007_25910
  SIC2007_25920
  SIC2007_25930
  SIC2007_25940
  SIC2007_25990
  SIC2007_26110
  SIC2007_26120
  SIC2007_26200
  SIC2007_26301
  SIC2007_26309
  SIC2007_26400
  SIC2007_26511
  SIC2007_26512
  SIC2007_26513
  SIC2007_26514
  SIC2007_26520
  SIC2007_26600
  SIC2007_26701
  SIC2007_26702
  SIC2007_26800
  SIC2007_27110
  SIC2007_27120
  SIC2007_27200
  SIC2007_27310
  SIC2007_27320
  SIC2007_27330
  SIC2007_27400
  SIC2007_27510
  SIC2007_27520
  SIC2007_27900
  SIC2007_28110
  SIC2007_28120
  SIC2007_28131
  SIC2007_28132
  SIC2007_28140
  SIC2007_28150
  SIC2007_28210
  SIC2007_28220
  SIC2007_28230
  SIC2007_28240
  SIC2007_28250
  SIC2007_28290
  SIC2007_28301
  SIC2007_28302
  SIC2007_28410
  SIC2007_28490
  SIC2007_28910
  SIC2007_28921
  SIC2007_28922
  SIC2007_28923
  SIC2007_28930
  SIC2007_28940
  SIC2007_28950
  SIC2007_28960
  SIC2007_28990
  SIC2007_29100
  SIC2007_29201
  SIC2007_29202
  SIC2007_29203
  SIC2007_29310
  SIC2007_29320
  SIC2007_30110
  SIC2007_30120
  SIC2007_30200
  SIC2007_30300
  SIC2007_30400
  SIC2007_30910
  SIC2007_30920
  SIC2007_30990
  SIC2007_31010
  SIC2007_31020
  SIC2007_31030
  SIC2007_31090
  SIC2007_32110
  SIC2007_32120
  SIC2007_32130
  SIC2007_32200
  SIC2007_32300
  SIC2007_32401
  SIC2007_32409
  SIC2007_32500
  SIC2007_32910
  SIC2007_32990
  SIC2007_33110
  SIC2007_33120
  SIC2007_33130
  SIC2007_33140
  SIC2007_33150
  SIC2007_33160
  SIC2007_33170
  SIC2007_33190
  SIC2007_33200
  SIC2007_35110
  SIC2007_35120
  SIC2007_35130
  SIC2007_35140
  SIC2007_35210
  SIC2007_35220
  SIC2007_35230
  SIC2007_35300
  SIC2007_36000
  SIC2007_37000
  SIC2007_38110
  SIC2007_38120
  SIC2007_38210
  SIC2007_38220
  SIC2007_38310
  SIC2007_38320
  SIC2007_39000
  SIC2007_41100
  SIC2007_41201
  SIC2007_41202
  SIC2007_42110
  SIC2007_42120
  SIC2007_42130
  SIC2007_42210
  SIC2007_42220
  SIC2007_42910
  SIC2007_42990
  SIC2007_43110
  SIC2007_43120
  SIC2007_43130
  SIC2007_43210
  SIC2007_43220
  SIC2007_43290
  SIC2007_43310
  SIC2007_43320
  SIC2007_43330
  SIC2007_43341
  SIC2007_43342
  SIC2007_43390
  SIC2007_43910
  SIC2007_43991
  SIC2007_43999
  SIC2007_45111
  SIC2007_45112
  SIC2007_45190
  SIC2007_45200
  SIC2007_45310
  SIC2007_45320
  SIC2007_45400
  SIC2007_46110
  SIC2007_46120
  SIC2007_46130
  SIC2007_46140
  SIC2007_46150
  SIC2007_46160
  SIC2007_46170
  SIC2007_46180
  SIC2007_46190
  SIC2007_46210
  SIC2007_46220
  SIC2007_46230
  SIC2007_46240
  SIC2007_46310
  SIC2007_46320
  SIC2007_46330
  SIC2007_46341
  SIC2007_46342
  SIC2007_46350
  SIC2007_46360
  SIC2007_46370
  SIC2007_46380
  SIC2007_46390
  SIC2007_46410
  SIC2007_46420
  SIC2007_46431
  SIC2007_46439
  SIC2007_46440
  SIC2007_46450
  SIC2007_46460
  SIC2007_46470
  SIC2007_46480
  SIC2007_46491
  SIC2007_46499
  SIC2007_46510
  SIC2007_46520
  SIC2007_46610
  SIC2007_46620
  SIC2007_46630
  SIC2007_46640
  SIC2007_46650
  SIC2007_46660
  SIC2007_46690
  SIC2007_46711
  SIC2007_46719
  SIC2007_46720
  SIC2007_46730
  SIC2007_46740
  SIC2007_46750
  SIC2007_46760
  SIC2007_46770
  SIC2007_46900
  SIC2007_47110
  SIC2007_47190
  SIC2007_47210
  SIC2007_47220
  SIC2007_47230
  SIC2007_47240
  SIC2007_47250
  SIC2007_47260
  SIC2007_47290
  SIC2007_47300
  SIC2007_47410
  SIC2007_47421
  SIC2007_47429
  SIC2007_47430
  SIC2007_47510
  SIC2007_47520
  SIC2007_47530
  SIC2007_47540
  SIC2007_47591
  SIC2007_47599
  SIC2007_47610
  SIC2007_47620
  SIC2007_47630
  SIC2007_47640
  SIC2007_47650
  SIC2007_47710
  SIC2007_47721
  SIC2007_47722
  SIC2007_47730
  SIC2007_47741
  SIC2007_47749
  SIC2007_47750
  SIC2007_47760
  SIC2007_47770
  SIC2007_47781
  SIC2007_47782
  SIC2007_47789
  SIC2007_47791
  SIC2007_47799
  SIC2007_47810
  SIC2007_47820
  SIC2007_47890
  SIC2007_47910
  SIC2007_47990
  SIC2007_49100
  SIC2007_49200
  SIC2007_49311
  SIC2007_49319
  SIC2007_49320
  SIC2007_49390
  SIC2007_49410
  SIC2007_49420
  SIC2007_49500
  SIC2007_50100
  SIC2007_50200
  SIC2007_50300
  SIC2007_50400
  SIC2007_51101
  SIC2007_51102
  SIC2007_51210
  SIC2007_51220
  SIC2007_52101
  SIC2007_52102
  SIC2007_52103
  SIC2007_52211
  SIC2007_52212
  SIC2007_52213
  SIC2007_52219
  SIC2007_52220
  SIC2007_52230
  SIC2007_52241
  SIC2007_52242
  SIC2007_52243
  SIC2007_52290
  SIC2007_53100
  SIC2007_53201
  SIC2007_53202
  SIC2007_55100
  SIC2007_55201
  SIC2007_55202
  SIC2007_55209
  SIC2007_55300
  SIC2007_55900
  SIC2007_56101
  SIC2007_56102
  SIC2007_56103
  SIC2007_56210
  SIC2007_56290
  SIC2007_56301
  SIC2007_56302
  SIC2007_58110
  SIC2007_58120
  SIC2007_58130
  SIC2007_58141
  SIC2007_58142
  SIC2007_58190
  SIC2007_58210
  SIC2007_58290
  SIC2007_59111
  SIC2007_59112
  SIC2007_59113
  SIC2007_59120
  SIC2007_59131
  SIC2007_59132
  SIC2007_59133
  SIC2007_59140
  SIC2007_59200
  SIC2007_60100
  SIC2007_60200
  SIC2007_61100
  SIC2007_61200
  SIC2007_61300
  SIC2007_61900
  SIC2007_62011
  SIC2007_62012
  SIC2007_62020
  SIC2007_62030
  SIC2007_62090
  SIC2007_63110
  SIC2007_63120
  SIC2007_63910
  SIC2007_63990
  SIC2007_64110
  SIC2007_64191
  SIC2007_64192
  SIC2007_64201
  SIC2007_64202
  SIC2007_64203
  SIC2007_64204
  SIC2007_64205
  SIC2007_64209
  SIC2007_64301
  SIC2007_64302
  SIC2007_64303
  SIC2007_64304
  SIC2007_64305
  SIC2007_64306
  SIC2007_64910
  SIC2007_64921
  SIC2007_64922
  SIC2007_64929
  SIC2007_64991
  SIC2007_64992
  SIC2007_64999
  SIC2007_65110
  SIC2007_65120
  SIC2007_65201
  SIC2007_65202
  SIC2007_65300
  SIC2007_66110
  SIC2007_66120
  SIC2007_66190
  SIC2007_66210
  SIC2007_66220
  SIC2007_66290
  SIC2007_66300
  SIC2007_68100
  SIC2007_68201
  SIC2007_68202
  SIC2007_68209
  SIC2007_68310
  SIC2007_68320
  SIC2007_69101
  SIC2007_69102
  SIC2007_69109
  SIC2007_69201
  SIC2007_69202
  SIC2007_69203
  SIC2007_70100
  SIC2007_70210
  SIC2007_70221
  SIC2007_70229
  SIC2007_71111
  SIC2007_71112
  SIC2007_71121
  SIC2007_71122
  SIC2007_71129
  SIC2007_71200
  SIC2007_72110
  SIC2007_72190
  SIC2007_72200
  SIC2007_73110
  SIC2007_73120
  SIC2007_73200
  SIC2007_74100
  SIC2007_74201
  SIC2007_74202
  SIC2007_74203
  SIC2007_74209
  SIC2007_74300
  SIC2007_74901
  SIC2007_74902
  SIC2007_74909
  SIC2007_74990
  SIC2007_75000
  SIC2007_77110
  SIC2007_77120
  SIC2007_77210
  SIC2007_77220
  SIC2007_77291
  SIC2007_77299
  SIC2007_77310
  SIC2007_77320
  SIC2007_77330
  SIC2007_77341
  SIC2007_77342
  SIC2007_77351
  SIC2007_77352
  SIC2007_77390
  SIC2007_77400
  SIC2007_78101
  SIC2007_78109
  SIC2007_78200
  SIC2007_78300
  SIC2007_79110
  SIC2007_79120
  SIC2007_79901
  SIC2007_79909
  SIC2007_80100
  SIC2007_80200
  SIC2007_80300
  SIC2007_81100
  SIC2007_81210
  SIC2007_81221
  SIC2007_81222
  SIC2007_81223
  SIC2007_81229
  SIC2007_81291
  SIC2007_81299
  SIC2007_81300
  SIC2007_82110
  SIC2007_82190
  SIC2007_82200
  SIC2007_82301
  SIC2007_82302
  SIC2007_82911
  SIC2007_82912
  SIC2007_82920
  SIC2007_82990
  SIC2007_84110
  SIC2007_84120
  SIC2007_84130
  SIC2007_84210
  SIC2007_84220
  SIC2007_84230
  SIC2007_84240
  SIC2007_84250
  SIC2007_84300
  SIC2007_85100
  SIC2007_85200
  SIC2007_85310
  SIC2007_85320
  SIC2007_85410
  SIC2007_85421
  SIC2007_85422
  SIC2007_85510
  SIC2007_85520
  SIC2007_85530
  SIC2007_85590
  SIC2007_85600
  SIC2007_86101
  SIC2007_86102
  SIC2007_86210
  SIC2007_86220
  SIC2007_86230
  SIC2007_86900
  SIC2007_87100
  SIC2007_87200
  SIC2007_87300
  SIC2007_87900
  SIC2007_88100
  SIC2007_88910
  SIC2007_88990
  SIC2007_90010
  SIC2007_90020
  SIC2007_90030
  SIC2007_90040
  SIC2007_91011
  SIC2007_91012
  SIC2007_91020
  SIC2007_91030
  SIC2007_91040
  SIC2007_92000
  SIC2007_93110
  SIC2007_93120
  SIC2007_93130
  SIC2007_93191
  SIC2007_93199
  SIC2007_93210
  SIC2007_93290
  SIC2007_94110
  SIC2007_94120
  SIC2007_94200
  SIC2007_94910
  SIC2007_94920
  SIC2007_94990
  SIC2007_95110
  SIC2007_95120
  SIC2007_95210
  SIC2007_95220
  SIC2007_95230
  SIC2007_95240
  SIC2007_95250
  SIC2007_95290
  SIC2007_96010
  SIC2007_96020
  SIC2007_96030
  SIC2007_96040
  SIC2007_96090
  SIC2007_97000
  SIC2007_98000
  SIC2007_98100
  SIC2007_98200
  SIC2007_99000
  SIC2007_99999
}
